<template>
  <v-container class="my-5">
    <!-- Notificaciones -->
    <v-snackbar
      v-model="errorMessage"
      color="error"
      top
      :timeout="4000"
    >
      {{ errorMessage }}
      <v-btn dark text @click="errorMessage = ''">Cerrar</v-btn>
    </v-snackbar>

    <v-snackbar
      v-model="successMessage"
      color="success"
      top
      :timeout="3000"
    >
      {{ successMessage }}
      <v-btn dark text @click="successMessage = ''">Cerrar</v-btn>
    </v-snackbar>

    <v-layout align-center justify-center fill-height>
      <v-btn color="primary" @click="openCreateDialog">
        <v-icon left>add</v-icon>
        Nuevo transporte
      </v-btn>
    </v-layout>

    <v-layout class="my-4" align-center justify-center fill-height>
      <v-flex>
        <v-card-title>
          Transporte de personal
          <v-spacer></v-spacer>
        <v-text-field
          v-model="searchQuery"
          append-icon="search"
          :label="$t('Buscar')"
          clearable
          single-line
          hide-details
        ></v-text-field>
        </v-card-title>

      <v-data-table
        :headers="headers"
        :items="filteredTransports"
        :loading="loading"
        :rows-per-page-text="$t('filas_por_pagina')"
        :rows-per-page-items="[
          { text: '5', value: 5 },
          { text: '10', value: 10 },
          { text: '25', value: 25 },
          { text: this.$t('todos'), value: -1 }
        ]"
        class="elevation-1"
      >
        <template slot="items" slot-scope="props">
          <td class="justify-center layout px-0">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon class="mr-2" v-on="on" @click="showQR(props.item)">qr_code</v-icon>
              </template>
              <span>Ver código QR</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon class="mr-2" v-on="on" @click="showDetails(props.item)">visibility</v-icon>
              </template>
              <span>Ver detalles</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon class="mr-2" v-on="on" @click="openEditDialog(props.item)">edit</v-icon>
              </template>
              <span>Editar transporte</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon color="red" v-on="on" @click="openDeleteDialog(props.item)">delete</v-icon>
              </template>
              <span>Eliminar transporte</span>
            </v-tooltip>
          </td>
          <td>{{ props.item.name || 'Sin nombre' }}</td>
          <td>{{ props.item.plate || 'Sin placa' }}</td>
          <td>{{ driverFullName(props.item) }}</td>
          <td class="text-xs-center">{{ props.item.capacity || 0 }}</td>
          <td class="text-xs-center">{{ accessCount(props.item) }}</td>
          <td class="text-xs-center">
            <v-chip :color="props.item.status === 'active' ? 'green' : 'red'" text-color="white" small>
              {{ props.item.status === 'active' ? 'Activo' : 'Inactivo' }}
            </v-chip>
          </td>
        </template>

        <template v-slot:no-data>
          <div class="text-center py-4">
            <p class="grey--text">No hay transportes registrados</p>
          </div>
        </template>
      </v-data-table>
      </v-flex>
    </v-layout>

    <!-- Componente para descargar el gafete QR personalizado -->
    <QRBadgeDownload
      v-if="selectedTransport"
      :show="showQRBadge"
      :transport="selectedTransport"
      :companyName="companyName"
      :companyLogo="companyLogo"
      @close="handleQRBadgeClose"
      @download-success="handleQRBadgeSuccess"
      @download-error="handleQRBadgeError"
    />

    <!-- Diálogo para ver detalles del transporte -->
    <v-dialog v-model="dialogDetails" max-width="700px" v-if="selectedTransport">
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>Detalles del Transporte</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="dialogDetails = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-4">
          <v-tabs v-model="activeTab">
            <!-- Tab: Información General -->
            <v-tab>Información General</v-tab>
            <v-tab>Accesos</v-tab>

            <!-- Información General -->
            <v-tab-item>
              <v-layout row wrap class="pa-4">
                <v-flex xs12 md6>
                  <div class="mb-3">
                    <strong>Nombre del Transporte:</strong>
                    <p>{{ selectedTransport.name }}</p>
                  </div>
                  <div class="mb-3">
                    <strong>Placa:</strong>
                    <p>{{ selectedTransport.plate }}</p>
                  </div>
                  <div class="mb-3">
                    <strong>Capacidad:</strong>
                    <p>{{ selectedTransport.capacity }} pasajeros</p>
                  </div>
                </v-flex>
                <v-flex xs12 md6>
                  <div class="mb-3">
                    <strong>Conductor:</strong>
                    <p>{{ selectedTransport.driver_name }} {{ selectedTransport.driver_last_name }}</p>
                  </div>
                  <div class="mb-3">
                    <strong>Estado:</strong>
                    <v-chip
                      :color="selectedTransport.status === 'active' ? 'green' : 'red'"
                      text-color="white"
                      small
                    >
                      {{ selectedTransport.status === 'active' ? 'Activo' : 'Inactivo' }}
                    </v-chip>
                  </div>
                  <div class="mb-3">
                    <strong>Creado:</strong>
                    <p>{{ formatDate(selectedTransport.created_at) }}</p>
                  </div>
                </v-flex>
              </v-layout>
            </v-tab-item>

            <!-- Accesos -->
            <v-tab-item>
              <v-card class="pt-4">
                <v-data-table
                  :headers="accessHeaders"
                  :items="selectedTransport.accesses || []"
                  :items-per-page="10"
                  class="elevation-1"
                >
                  <template slot="items" slot-scope="props">
                    <td>{{ props.item.date }}</td>
                    <td>{{ props.item.location }}</td>
                    <td class="text-xs-center">
                      <v-chip :color="props.item.type === 'entrada' ? 'green' : 'red'" text-color="white" small>
                        {{ props.item.type === 'entrada' ? 'Entrada' : 'Salida' }}
                      </v-chip>
                    </td>
                  </template>

                  <template v-slot:no-data>
                    <div class="text-center py-4">
                      <p class="grey--text">No hay accesos registrados</p>
                    </div>
                  </template>
                </v-data-table>
              </v-card>
            </v-tab-item>
          </v-tabs>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Diálogo de confirmación para eliminar -->
    <v-dialog v-model="dialogDelete" max-width="400">
      <v-card>
        <v-card-title class="headline">Confirmar eliminación</v-card-title>
        <v-card-text>
          ¿Está seguro de que desea eliminar el transporte "{{ deleteTransportName }}"? Esta acción no se puede deshacer.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="dialogDelete = false">Cancelar</v-btn>
          <v-btn color="error" @click="confirmDelete">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Formulario para crear/editar -->
    <PersonalTransportForm
      ref="formComponent"
      :showDialog="dialogForm"
      :transport="editingTransport"
      @save="handleFormSave"
      @close="closeFormDialog"
    />
  </v-container>
</template>

<script>
import transportService from '../../services/transportService';
import PersonalTransportForm from '../../components/PersonalTransportForm.vue';
import QRBadgeDownload from '../../components/QRBadgeDownload.vue';

export default {
  name: 'PersonalTransport',
  components: {
    PersonalTransportForm,
    QRBadgeDownload
  },
  data() {
    return {
      loading: false,
      searchQuery: '',
      dialogForm: false,
      dialogDetails: false,
      dialogDelete: false,
      activeTab: 0,
      selectedTransport: null,
      editingTransport: null,
      deleteTransportId: null,
      deleteTransportName: '',
      transports: [],
      errorMessage: '',
      successMessage: '',
      showQRBadge: false,
      companyLogo: null,
      companyName: '',
      headers: [
        { text: 'Acciones', value: 'actions', align: 'center', sortable: false },
        { text: 'Nombre', value: 'name', align: 'left' },
        { text: 'Placa', value: 'plate', align: 'left' },
        { text: 'Conductor', value: 'driver', align: 'left' },
        { text: 'Capacidad', value: 'capacity', align: 'center' },
        { text: 'Accesos', value: 'accesses', align: 'center' },
        { text: 'Estado', value: 'status', align: 'center' }
      ],
      accessHeaders: [
        { text: 'Fecha y Hora', value: 'date', align: 'left' },
        { text: 'Ubicación', value: 'location', align: 'left' },
        { text: 'Tipo', value: 'type', align: 'center' }
      ]
    };
  },
  computed: {
    filteredTransports() {
      if (!this.searchQuery) {
        return this.transports;
      }
      const query = this.searchQuery.toLowerCase();
      return this.transports.filter(transport => [
        transport.name,
        transport.plate,
        this.driverFullName(transport)
      ].some(value => String(value || '').toLowerCase().includes(query)));
    }
  },
  mounted() {
    this.loadTransports();
    this.loadCompanyInfo();
  },
  methods: {
    async loadTransports() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const response = await transportService.getTransports();
        let results = [];

        if (Array.isArray(response)) {
          results = response;
        } else if (response.results && Array.isArray(response.results)) {
          results = response.results;
        }

        this.transports = results.map(t =>
          transportService.mapTransportToFrontend(t)
        );
      } catch (error) {
        this.errorMessage = error.message || 'Error cargando transportes';
        console.error('Error:', error);
      } finally {
        this.loading = false;
      }
    },
    openCreateDialog() {
      this.editingTransport = null;
      this.dialogForm = true;
    },
    openEditDialog(transport) {
      this.editingTransport = { ...transport };
      this.dialogForm = true;
    },
    closeFormDialog() {
      this.dialogForm = false;
      this.editingTransport = null;
    },
    async handleFormSave(formData) {
      this.errorMessage = '';
      try {
        if (this.editingTransport) {
          await transportService.updateTransport(this.editingTransport.id, formData);
          this.successMessage = 'Transporte actualizado exitosamente';
        } else {
          await transportService.createTransport(formData);
          this.successMessage = 'Transporte creado exitosamente';
        }
        await this.loadTransports();
        this.closeFormDialog();
        setTimeout(() => this.successMessage = '', 3000);
      } catch (error) {
        this.errorMessage = error.message || 'Error guardando transporte';
        console.error('Error:', error);
      }
    },
    loadCompanyInfo() {
      try {
        if (this.$store.state.user && this.$store.state.user.profile) {
          const profile = this.$store.state.user.profile;
          this.companyName = profile.name_company || profile.name_company || 'Mi Empresa';
          this.companyLogo = profile.logo || profile.logo || null;
        } else if (this.$store.getters.getProfile) {
          const profile = this.$store.getters.getProfile;
          this.companyName = profile.name_company || profile.name_company || 'Mi Empresa';
          this.companyLogo = profile.logo || profile.logo || null;
        }
      } catch (error) {
        console.error('Error cargando información de empresa:', error);
        this.companyName = 'Mi Empresa';
      }
    },
    showQR(transport) {
      this.selectedTransport = transport;
      this.showQRBadge = true;
    },
    showDetails(transport) {
      this.selectedTransport = transport;
      this.dialogDetails = true;
      this.activeTab = 0;
    },
    openDeleteDialog(transport) {
      this.deleteTransportId = transport.id;
      this.deleteTransportName = transport.name;
      this.dialogDelete = true;
    },
    async confirmDelete() {
      if (this.deleteTransportId) {
        this.errorMessage = '';
        try {
          await transportService.deleteTransport(this.deleteTransportId);
          this.successMessage = 'Transporte eliminado exitosamente';
          await this.loadTransports();
          this.dialogDelete = false;
          setTimeout(() => this.successMessage = '', 3000);
        } catch (error) {
          this.errorMessage = error.message || 'Error eliminando transporte';
          console.error('Error:', error);
        }
      }
    },
    driverFullName(transport) {
      const driverName = [transport.driver_name, transport.driver_last_name]
        .filter(Boolean)
        .join(' ');
      return driverName || 'Sin conductor asignado';
    },
    accessCount(transport) {
      return Array.isArray(transport.accesses) ? transport.accesses.length : 0;
    },
    formatDate(date) {
      if (!date) return 'Sin fecha';
      const normalizedDate = new Date(`${date}T00:00:00`);
      return Number.isNaN(normalizedDate.getTime())
        ? date
        : normalizedDate.toLocaleDateString('es-MX', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        });
    },
    handleQRBadgeClose() {
      this.showQRBadge = false;
    },
    handleQRBadgeSuccess(message) {
      this.successMessage = message;
      setTimeout(() => this.successMessage = '', 3000);
    },
    handleQRBadgeError(message) {
      this.errorMessage = message;
    }
  }
};
</script>

<style scoped>

.mt-3 {
  margin-top: 12px;
}

.pa-4 {
  padding: 16px;
}

.pt-4 {
  padding-top: 16px;
}

.my-5 {
  margin-top: 25px;
  margin-bottom: 25px;
}

.text-right {
  text-align: right;
}

.text-center {
  text-align: center;
}

.mx-auto {
  margin-left: auto;
  margin-right: auto;
}

.py-4 {
  padding-top: 16px;
  padding-bottom: 16px;
}

.grey--text {
  color: #9e9e9e;
}

.font-weight-bold {
  font-weight: bold;
}

@media (max-width: 600px) {
  .transport-page {
    margin-top: 20px;
  }

  .page-heading,
  .table-toolbar {
    align-items: stretch;
    flex-direction: column;
    gap: 16px;
  }

  .create-button {
    align-self: flex-start;
  }

  .table-toolbar {
    padding: 16px;
  }

  .transport-search {
    max-width: none;
    width: 100%;
  }
}
</style>
